import { useQuoteStore } from '../../store/useQuoteStore';
import { PRODUCTS, subscribeCatalog, getCatalogVersion } from '../../data/products';
import { getProductType } from '../../data/productMeta';
import { useMemo, useSyncExternalStore } from 'react';
import LineItemRow from './LineItemRow';
import AdhocItemRow from './AdhocItemRow';
import { useVendors } from '../../modules/vendors/hooks/useVendors';

export default function LineItemList() {
  const items = useQuoteStore((state) => state.items);
  const addItem = useQuoteStore((state) => state.addItem);
  const addAdhocItem = useQuoteStore((state) => state.addAdhocItem);

  // Loaded once for the adhoc-line purchase requisition vendor picker.
  // Catalog rows ignore it; the fetch is skipped from re-running by memo.
  const { vendors, loading: vendorsLoading } = useVendors();

  // PRODUCTS is spliced in-place, so depend on the version counter (bumped by productService)
  // instead of the array reference. Otherwise the memo would never re-evaluate after Firestore hydrates.
  const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);
  const { AVAILABLE_CAT1 } = useMemo(() => {
    // Category pickers are driven by selectable LINE products only (not add-ons).
    // Add-ons attach via the per-line add-on picker after a base is chosen — they
    // are never a standalone line — so the sheet's "Add-on" cat2 must not surface
    // as a category option (it would dead-end to an empty product list).
    const selectable = PRODUCTS.filter((p) => getProductType(p.prd_code) !== 'addon');
    const cat1s = Array.from(new Set(selectable.map((p) => p.cat1)));
    return { AVAILABLE_CAT1: cat1s };
    // PRODUCTS is a stable reference that mutates in place; catalogVersion is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogVersion]);

  return (
    <section className="bg-white border border-outline-variant rounded-xl p-8 shadow-sm">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6 pb-4 border-b border-outline-variant">
        <div>
          <h2 className="text-xl font-bold font-headline text-secondary mb-1">Products</h2>
          <p className="text-outline font-medium text-xs uppercase tracking-widest">
            Line items and pricing
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={addAdhocItem}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/40 text-primary font-bold text-sm hover:bg-primary/5 transition-colors"
            title="Add a free-text item that isn't in the catalog"
          >
            <span className="material-symbols-outlined text-sm font-bold">edit_note</span>
            Adhoc Item
          </button>
          <button
            onClick={addItem}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-container text-primary font-bold text-sm hover:opacity-90 transition-opacity"
          >
            <span className="material-symbols-outlined text-sm font-bold">add</span>
            Add Item
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-outline font-medium text-sm bg-surface-container-highest rounded-lg border border-dashed border-outline-variant">
          No items added yet. Click &quot;Add Item&quot; for a catalog product, or &quot;Adhoc
          Item&quot; for something custom.
        </div>
      ) : (
        <div className="space-y-6">
          {items.map((item, index) =>
            item.source === 'adhoc' ? (
              <AdhocItemRow
                key={item.id}
                item={item}
                index={index}
                vendors={vendors}
                vendorsLoading={vendorsLoading}
              />
            ) : (
              <LineItemRow key={item.id} item={item} index={index} cat1Options={AVAILABLE_CAT1} />
            ),
          )}
        </div>
      )}
    </section>
  );
}
